import { Inject, Injectable, Optional } from '@angular/core';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { Observable, map } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ConstantUtil } from 'src/app/util/constant.util';

import {
  ChatExportConfig,
  ChatExportEligibility,
  ChatExportStatus,
  ChatExportTask,
  DeleteChatExportResponse,
  DownloadGrantResult,
  SubmitChatExportResponse
} from '../models/chat-export.model';

@Injectable({
  providedIn: 'root'
})
export class ChatExportService {
  constructor(
    private readonly httpService: HttpService,
    @Optional() @Inject(ALAIN_I18N_TOKEN) private readonly i18n?: I18NService
  ) {}

  /** 获取服务号对话导出配置（时区、最大可选月份、管理者权限） */
  getConfig(serviceNumberId: string): Observable<ChatExportConfig> {
    return this.httpService
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHAT_EXPORT_CONFIG, { serviceNumberId })
      .pipe(map(res => (res && res.success ? (res.data as ChatExportConfig) : ({} as ChatExportConfig))));
  }

  /** 获取服务号历史导出记录列表 */
  getRecords(serviceNumberId: string): Observable<ChatExportTask[]> {
    return this.httpService
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHAT_EXPORT_RECORDS, { serviceNumberId })
      .pipe(map(res => (res && res.success && Array.isArray(res.data) ? (res.data as ChatExportTask[]) : [])));
  }

  /** 提交整月对话导出任务 */
  submitExport(serviceNumberId: string, month: string): Observable<SubmitChatExportResponse> {
    return this.httpService
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHAT_EXPORT_SUBMIT, { serviceNumberId, month })
      .pipe(map(res => (res && res.success ? (res.data as SubmitChatExportResponse) : ({} as SubmitChatExportResponse))));
  }

  /** 申请短效 GCS 下载签名直链 (TTL ≤ 5分钟) */
  authorizeDownload(taskId: string): Observable<DownloadGrantResult> {
    return this.httpService
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHAT_EXPORT_AUTHORIZE_DOWNLOAD, { taskId })
      .pipe(map(res => (res && res.success ? (res.data as DownloadGrantResult) : ({} as DownloadGrantResult))));
  }

  /** 请求删除指定导出任务及云端产物 */
  deleteExport(taskId: string): Observable<DeleteChatExportResponse> {
    return this.httpService
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHAT_EXPORT_DELETE, { taskId })
      .pipe(map(res => (res && res.success ? (res.data as DeleteChatExportResponse) : ({} as DeleteChatExportResponse))));
  }

  /** 计算下一个月的 YYYY-MM 格式 */
  getNextMonth(month: string): string {
    if (!month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return '';
    const [y, m] = month.split('-').map(Number);
    return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  }

  /** 获取整月半开区间说明文本 */
  getMonthRangeText(month: string): string {
    if (!month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return '';
    const next = this.getNextMonth(month);
    const start = `${month}-01 00:00`;
    const end = `${next}-01 00:00`;
    if (this.i18n) {
      return this.i18n.fanyi('servicenumber.chat-export.month-range-desc', { start, end });
    }
    return `${start} 至 ${end} 前（完整日曆月）`;
  }

  /** 评估所选月份在当前号状态下的可导出性 */
  checkEligibility(month: string, maxMonth: string, records: ChatExportTask[] = [], canManage: boolean = true): ChatExportEligibility {
    if (!canManage) {
      return ChatExportEligibility.NO_PERMISSION;
    }
    if (!month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month) || (maxMonth && month > maxMonth)) {
      return ChatExportEligibility.INVALID_MONTH;
    }

    // 检查当前所选月份的既有任务
    const monthTask = records.find(t => t.month === month);
    if (monthTask) {
      if (monthTask.isDeleting) {
        return ChatExportEligibility.IS_DELETING;
      }
      if (monthTask.status === ChatExportStatus.READY) {
        return ChatExportEligibility.HAS_READY;
      }
      if (monthTask.status === ChatExportStatus.PROCESSING) {
        return ChatExportEligibility.IS_PROCESSING;
      }
    }

    // 检查同号是否有其他月份处于处理中状态（同号单任务保护）
    const hasOtherProcessing = records.some(t => t.month !== month && t.status === ChatExportStatus.PROCESSING && !t.isDeleting);
    if (hasOtherProcessing) {
      return ChatExportEligibility.HAS_OTHER_PROCESSING;
    }

    return ChatExportEligibility.CAN_EXPORT;
  }
}
